import { type Incident, IncidentStatus } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { Async } from '@piccolohealth/util';
import React from 'react';
import { FaRegFilePdf } from 'react-icons/fa';
import { useGenerateIncidentPdfMutation } from '../../graphql/hooks/useGenerateIncidentPdfMutation';
import { useAppContext } from '../../hooks/useAppContext';

interface Props {
  incident: Incident;
}

export const IncidentPdfMenuItem = (props: Props) => {
  const { incident } = props;
  const { organization, promiseToast } = useAppContext();
  const mutation = useGenerateIncidentPdfMutation();

  const isDraft = incident.status === IncidentStatus.AwaitingReview;

  const onClick = React.useCallback(async () => {
    const promise = async () => {
      const resp = await mutation.mutateAsync({
        organizationId: organization.id,
        incidentId: incident.id,
      });
      await Async.delay(500);
      const { viewUrl, message } = resp.generateIncidentPdf;
      window.open(viewUrl || '', '_blank');
      return message;
    };

    await promiseToast(promise, {
      success: (resp: string | null) => {
        if (!resp) {
          return {
            title: 'PDF successfully generated',
            duration: 2000,
          };
        }

        return {
          title: 'PDF generated with warnings',
          description: resp,
          duration: 8000,
        };
      },
      error: {
        title: 'Failed to generate PDF',
      },
      loading: {
        title: 'Generating PDF...',
      },
    });
  }, [mutation, organization.id, incident.id, promiseToast]);

  return (
    <ChakraV3.MenuItem value='generate-incident-pdf' onClick={onClick}>
      <FaRegFilePdf />
      {isDraft ? 'Generate draft PDF' : 'Generate PDF'}
    </ChakraV3.MenuItem>
  );
};
